import { useMemo, useState } from 'react';
import type { GameCategory, GolfGameFormat } from '@/lib/reference/golf-games';
import { GAMES_CATEGORIES } from '@/lib/reference/golf-games';

export const FORMAT_CAT_STYLES: Record<GameCategory, string> = {
  Format: 'bg-navy/10 text-navy',
  Wager: 'bg-gold/20 text-gold-deep',
  Team: 'bg-good/15 text-good',
  'Side Bet': 'bg-bad/10 text-bad',
};

interface Props {
  formats: GolfGameFormat[];
  selectedId: string;
  onSelect: (id: string) => void;
  showCategoryFilter?: boolean;
  label?: string;
}

export function GameFormatGrid({
  formats,
  selectedId,
  onSelect,
  showCategoryFilter = true,
  label = 'Format',
}: Props) {
  const categories = useMemo(() => {
    if (!showCategoryFilter) return ['all'] as const;
    const present = new Set(formats.map((f) => f.cat));
    return GAMES_CATEGORIES.filter((c) => c === 'all' || present.has(c));
  }, [formats, showCategoryFilter]);

  const [category, setCategory] = useState<GameCategory | 'all'>('all');

  const visible = useMemo(
    () => (category === 'all' ? formats : formats.filter((f) => f.cat === category)),
    [formats, category],
  );

  const selected = formats.find((f) => f.id === selectedId);

  return (
    <div>
      <div className="mb-2 text-xs font-bold uppercase tracking-wide text-mute">{label}</div>
      {showCategoryFilter && categories.length > 2 && (
        <div className="mb-2.5 flex gap-1.5 overflow-x-auto pb-1 no-scrollbar">
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setCategory(cat)}
              className={`shrink-0 rounded-full px-3.5 py-2 text-xs font-semibold ${
                category === cat ? 'bg-navy text-cream shadow-bt' : 'bt-flat border border-line bg-paper text-ink-soft'
              }`}
            >
              {cat === 'all' ? `All (${formats.length})` : cat}
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2.5">
        {visible.map((format) => {
          const on = format.id === selectedId;
          return (
            <button
              key={format.id}
              type="button"
              onClick={() => onSelect(format.id)}
              className={`flex h-full w-full flex-col gap-1.5 rounded-[14px] p-3 text-left shadow-bt ${
                on ? 'border-2 border-navy bg-navy/5' : 'border border-line bg-paper'
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                  <div className="font-serif text-base font-bold tracking-[-0.015em] text-ink">{format.name}</div>
                  <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase ${FORMAT_CAT_STYLES[format.cat]}`}>
                    {format.cat}
                  </span>
                </div>
                <p className="line-clamp-2 text-[11.5px] leading-[1.35] text-ink-soft">{format.summary}</p>
                <div className="mt-auto flex flex-wrap gap-2 pt-2 text-[10.5px] text-mute">
                  <span>👥 {format.players}</span>
                  <span>⚙️ {'●'.repeat(format.complexity)}{'○'.repeat(5 - format.complexity)}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      {selected && (
        <p className="mt-2.5 text-[11px] leading-relaxed text-mute">{selected.summary}</p>
      )}
    </div>
  );
}
